import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { apolloClient } from '../Root';

const GET_PAYMENT_STATUS = gql`
  query GetStorefrontRentalPaymentStatus($rentalToken: String!) {
    getStorefrontRentalPaymentStatus(rentalToken: $rentalToken) {
      status
      transactionId
      errorMessage
    }
  }
`;

const POLL_INTERVAL_MS = 3000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const CartPaymentStatusPoller = ({ rentalToken, retryUrl, onSuccess, children }) => {
  const [status, setStatus] = useState('processing');
  const [errorMessage, setErrorMessage] = useState(null);
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (!rentalToken) {
      setStatus('success');
      return undefined;
    }

    let cancelled = false;
    let intervalId;
    let timeoutId;

    const poll = async () => {
      try {
        const { data } = await apolloClient.query({
          query: GET_PAYMENT_STATUS,
          variables: { rentalToken },
          fetchPolicy: 'network-only',
        });
        if (cancelled) return;
        const result = data && data.getStorefrontRentalPaymentStatus;
        if (!result) return;
        if (result.status === 'success' || result.status === 'failed') {
          setStatus(result.status);
          setErrorMessage(result.errorMessage);
          clearInterval(intervalId);
          clearTimeout(timeoutId);
        }
      } catch (err) {
        // Swallow transient network errors and keep polling.
        // A terminal failure surfaces via the timeout branch below.
        // eslint-disable-next-line no-console
        console.error('Error polling payment status:', err);
      }
    };

    poll();
    intervalId = setInterval(poll, POLL_INTERVAL_MS);
    timeoutId = setTimeout(() => {
      setTimedOut(true);
      clearInterval(intervalId);
    }, POLL_TIMEOUT_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [rentalToken]);

  useEffect(() => {
    if (status === 'success' && onSuccess) onSuccess();
  }, [status]);

  if (status === 'success') {
    return children;
  }

  if (status === 'failed') {
    return (
      <div className='paymentStatus paymentStatusFailed'>
        <h2>Payment Failed</h2>
        <p>{errorMessage || 'Your payment could not be processed.'}</p>
        {retryUrl && (
          <a className='btn primary' href={retryUrl}>
            Retry Payment
          </a>
        )}
      </div>
    );
  }

  return (
    <div className='paymentStatus paymentStatusProcessing'>
      <h2>Your payment is being processed</h2>
      <p>
        {timedOut
          ? 'This is taking longer than usual. Please check your email for confirmation, or visit your order page later.'
          : "This usually takes a few seconds. Please don't close this page."}
      </p>
    </div>
  );
};

CartPaymentStatusPoller.propTypes = {
  rentalToken: PropTypes.string,
  retryUrl: PropTypes.string,
  onSuccess: PropTypes.func,
  children: PropTypes.node,
};

CartPaymentStatusPoller.defaultProps = {
  rentalToken: null,
  retryUrl: null,
  onSuccess: null,
  children: null,
};

export default CartPaymentStatusPoller;
